/**
 * Auth State Monitor Component
 * 
 * RESPONSIBILITY: Monitor auth state and handle auto-logout/redirection
 * 
 * This component listens to changes in authentication state and handles
 * navigation when the user is logged out (e.g., when tokens are cleared
 * by the axios interceptor due to 401 errors).
 * 
 * This is the CORRECT place for handling auth-based navigation in response
 * to state changes, keeping navigation logic out of the HTTP layer.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { sessionExpired } from '../../store/features/auth/authSlice';
import { designSyncService } from '../../services/designSyncService';
import { tokenStore, E2E_ACCESS_TOKEN_SESSION_KEY } from '../../services/tokenStore';
import { trackEvent } from '../../utils/analytics';
import { ConflictResolutionModal } from './ConflictResolutionModal';
import { designService } from '../../services/api/design.service';
import { setFrontCanvasJSON, setBackCanvasJSON } from '../../store/canvasObjectSlice';
import { toast } from 'react-hot-toast';
import { ReturningUserWelcome } from '../checkout/ReturningUserWelcome';
import { fetchWishlist, resetWishlist } from '../../store/wishlistSlice';
import { syncCartAfterLogin } from '../../store/cartSlice';
import { selectAuthBootstrapComplete } from '../../store/features/auth';
import {
  getLoginRedirectState,
  resolvePostLoginRedirect,
} from '../../utils/authReturnUrl';

export function AuthStateMonitor() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user, isLoading: authLoading } = useAppSelector((state) => state.auth);
  const bootstrapComplete = useAppSelector(selectAuthBootstrapComplete);
  
  // Track previous auth state to detect login event (null = not seeded yet after bootstrap)
  const prevAuthRef = useRef<boolean | null>(null);

  // ❤️ Wishlist: hydrate when session is known; clear on logout (navbar + hearts stay in sync)
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      dispatch(resetWishlist());
      return;
    }
    dispatch(fetchWishlist());
  }, [authLoading, isAuthenticated, dispatch]);
  
  // Conflict resolution state
  const [conflictData, setConflictData] = useState<{
    local: any;
    cloud: any;
  } | null>(null);
  
  // Returning user welcome modal state
  const [showReturningUserWelcome, setShowReturningUserWelcome] = useState(false);

  // Auto-sync guest designs after successful login (not session restore on cold load)
  useEffect(() => {
    if (authLoading || !bootstrapComplete) return;

    const prev = prevAuthRef.current;
    prevAuthRef.current = isAuthenticated;

    if (prev === null) {
      return;
    }

    const wasGuest = prev === false;
    const isNowAuthenticated = isAuthenticated;

    // Detect transition from guest to authenticated (login event)
    if (wasGuest && isNowAuthenticated) {
      const e2eSessionBootstrap =
        typeof sessionStorage !== 'undefined' &&
        Boolean(sessionStorage.getItem(E2E_ACCESS_TOKEN_SESSION_KEY));
      if (e2eSessionBootstrap) {
        return;
      }

      console.log('✅ User logged in - checking for guest designs to sync');
      
      // If user data is not loaded yet (e.g., OAuth popup login),
      // fetch it from backend
      if (!user && isAuthenticated) {
        console.log('📥 Fetching user data after OAuth login...');
        import('../../store/features/auth/authThunks').then(({ getCurrentUser }) => {
          dispatch(getCurrentUser());
        });
      }
      
      // Wrap async operations in IIFE
      (async () => {
        // Check for guest design in localStorage (created when clicking Preview as guest)
        const guestDesignId = localStorage.getItem('guest_design_id');
        
        if (guestDesignId) {
          console.log('🔗 Claiming guest design after login:', guestDesignId);
          
          try {
            // Claim the guest design (attaches it to the user)
            await designService.claimDesign(guestDesignId);
            
            trackEvent('draft.claimed', {
              designId: guestDesignId,
              claimMethod: 'after_login',
            });
            
            console.log('✅ Guest design claimed successfully');
            localStorage.removeItem('guest_design_id'); // Clean up
          } catch (error) {
            console.error('❌ Failed to claim guest design:', error);
          }
        }
        
        // Also sync any local localStorage design (fallback for old flow)
        const localDesign = designSyncService.getLocalDesign();
        
        if (localDesign) {
          try {
            // Fetch user's designs to check for conflicts
            const userDesigns = await designService.getUserDesigns();
            
            if (userDesigns && userDesigns.length > 0) {
              // Get the most recent design
              const latestCloud = userDesigns.sort((a: any, b: any) => 
                new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
              )[0];
              
              const localTime = new Date(localDesign.timestamp);
              const cloudTime = new Date(latestCloud.updatedAt);
              
              // Check if cloud is newer
              if (cloudTime > localTime) {
                console.log('⚠️ Conflict detected: cloud is newer');
                setConflictData({
                  local: localDesign,
                  cloud: latestCloud,
                });
                return; // Don't auto-sync, wait for user decision
              }
            }
            
            // No conflict, sync normally
            designSyncService.syncAfterLogin();
            trackEvent('draft.claimed', {
              timestamp: localDesign.timestamp,
              guestId: localDesign.id,
              claimMethod: 'localStorage_sync',
            });
          } catch (error) {
            console.error('Failed to check for conflicts:', error);
            // On error, try to sync anyway
            designSyncService.syncAfterLogin();
          }
        }
      })();

      // Merge guest cart once when a guest session exists; no eager server refresh otherwise.
      void dispatch(syncCartAfterLogin());

      const authAction = localStorage.getItem('auth_action');
      const authRedirect = localStorage.getItem('auth_redirect');
      const postLoginDestination = resolvePostLoginRedirect({
        locationState: location.state,
        storedAuthRedirect: authRedirect,
        userRoles: user?.roles,
        currentPathname: location.pathname,
      });

      const isSeller = user?.roles?.includes('seller');
      const needsSellerOnboarding =
        isSeller && !user?.sellerOnboardingCompletedAt;

      if (
        needsSellerOnboarding &&
        location.pathname !== '/seller/onboarding' &&
        (!postLoginDestination ||
          postLoginDestination.startsWith('/seller'))
      ) {
        navigate('/seller/onboarding', {
          replace: true,
          state: { from: getLoginRedirectState(location.state).from ?? location },
        });
        return;
      }

      if (postLoginDestination && location.pathname !== postLoginDestination) {
        if (authAction && authRedirect) {
          localStorage.removeItem('auth_action');
          localStorage.removeItem('auth_redirect');
          trackEvent('login.success.redirected', {
            action: authAction,
            redirectTo: postLoginDestination,
          });
        } else {
          const { from } = getLoginRedirectState(location.state);
          trackEvent('login.success.redirected', {
            action: 'return_url',
            redirectTo: postLoginDestination,
            fromPath: from?.pathname,
          });
        }

        console.log('🔄 Redirecting after login to:', postLoginDestination);
        navigate(postLoginDestination, { replace: true });

        if (authAction === 'complete_checkout' && authRedirect === '/checkout') {
          setTimeout(() => {
            setShowReturningUserWelcome(true);
          }, 1000);
        }
        return;
      }

      if (authAction && authRedirect) {
        localStorage.removeItem('auth_action');
        localStorage.removeItem('auth_redirect');
      }
    }
  }, [
    authLoading,
    bootstrapComplete,
    isAuthenticated,
    user,
    navigate,
    location.pathname,
    location.state,
    dispatch,
  ]);

  // Handle auth state inconsistency (session expiration)
  useEffect(() => {
    if (!isAuthenticated) return;

    const accessToken = tokenStore.getAccessToken();
    if (accessToken) return;

    console.warn('🔒 Auth state inconsistency detected - session expired');

    dispatch(sessionExpired('Your session has expired. Please sign in again.'));

    const publicPages = ['/login', '/register', '/forgot-password', '/', '/shop', '/about'];
    const isOnPublicPage = publicPages.some((page) => location.pathname.startsWith(page));

    if (!isOnPublicPage) {
      navigate('/login', {
        replace: true,
        state: {
          message: 'Your session has expired. Please sign in again.',
          from: location,
        },
      });
    }
  }, [isAuthenticated, location, navigate, dispatch]);

  // Handle conflict resolution
  const handleConflictResolve = async (choice: 'local' | 'cloud' | 'compare') => {
    if (!conflictData) return;
    
    try {
      if (choice === 'cloud') {
        // Load cloud version into editor
        dispatch(setFrontCanvasJSON(conflictData.cloud.frontSide));
        dispatch(setBackCanvasJSON(conflictData.cloud.backSide));
        designSyncService.clearLocalDesign();
        toast.success('Cloud version loaded');
      } else if (choice === 'local') {
        // Upload local as new version
        await designSyncService.syncAfterLogin();
        toast.success('Local version uploaded');
      } else if (choice === 'compare') {
        // For now, just show both thumbnails (can be enhanced later)
        toast.info('Opening comparison view...');
        // TODO: Implement split-view comparison
      }
    } catch (error) {
      console.error('Failed to resolve conflict:', error);
      toast.error('Failed to resolve conflict');
    } finally {
      setConflictData(null);
    }
  };
  
  // Render conflict modal if needed
  if (conflictData) {
    return (
      <ConflictResolutionModal
        local={conflictData.local}
        cloud={conflictData.cloud}
        onResolve={handleConflictResolve}
        onClose={() => setConflictData(null)}
      />
    );
  }
  
  // Render returning user welcome if needed
  if (showReturningUserWelcome) {
    return (
      <ReturningUserWelcome
        onClose={() => setShowReturningUserWelcome(false)}
      />
    );
  }
  
  return null;
}

/**
 * How this works:
 * 
 * 1. User makes API call
 * 2. API returns 401 (unauthorized)
 * 3. Axios interceptor clears localStorage tokens (but not Redux state)
 * 4. This component detects the mismatch
 * 5. Clears Redux auth state
 * 6. Navigates to login (using React Router - no page refresh!)
 * 
 * Benefits:
 * - Separation of concerns (HTTP layer doesn't handle navigation)
 * - Uses React Router (smooth navigation, no page refresh)
 * - Can show custom messages
 * - Easy to test and extend
 */

